import { req } from "./transport";
import type { PostKind, OwnerPostSummary, AuthUser } from "./types";

export const catalogApi = {
  // Increment 37: categories
  async categories(group?: string) {
    const r = await req<{ categories: any[] }>(`/categories${group ? `?group=${encodeURIComponent(group)}` : ""}`);
    return r?.categories ?? [];
  },

  async listPage(
    kind: "people" | "services" | "companies" | "jobs" | "projects" | "rfqs",
    params?: Record<string, string | string[] | number | boolean | undefined>,
    signal?: AbortSignal,
  ) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params || {})) {
      if (Array.isArray(v)) v.forEach((value) => qs.append(k, value));
      else if (v !== undefined && v !== "" && v !== false) qs.set(k, String(v));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    // Abortable foreground reads must distinguish an expired session from an empty directory.
    const page = await req<{ items: any[]; total: number; nextCursor?: string | null }>(`/marketplace/${kind}${suffix}`, { signal }, { rejectUnauthorized: signal !== undefined });
    if (signal && !page) throw new Error("Missing marketplace page.");
    return page ?? { items: [], total: 0, nextCursor: null };
  },

  async listPublicProfiles(params?: Record<string, string | number | boolean | undefined>) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params || {})) {
      if (v !== undefined && v !== "" && v !== false) qs.set(k, String(v));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return (await req<{ items: any[]; total: number; nextCursor?: string | null }>(`/marketplace/profiles${suffix}`))
      ?? { items: [], total: 0, nextCursor: null };
  },

  async list(
    kind: "people" | "services" | "companies" | "jobs" | "projects" | "rfqs",
    params?: Record<string, string | number | boolean | undefined>,
    signal?: AbortSignal,
  ) {
    const page = await this.listPage(kind, params, signal);
    if (signal && !Array.isArray(page?.items)) throw new Error("Missing marketplace rows.");
    return page.items;
  },

  // [#29] Live per-category service counts for the landing page cards.
  async categoryCounts(signal?: AbortSignal) {
    return req<{ counts: Record<string, number>; total: number }>(`/marketplace/category-counts`, { signal }, { rejectUnauthorized: signal !== undefined });
  },

  // Increment 2: create a listing (throws on 4xx with the server message)
  async createListing(endpoint: string, payload: Record<string, unknown>) {
    return req<{ item: any; kind: string }>(`/listings/${endpoint}`, { method: "POST", body: JSON.stringify(payload) });
  },

  async listMine(signal?: AbortSignal) {
    return req<{ jobs: any[]; services: any[]; projects: any[]; people: any[]; companies: any[]; rfqs: any[] }>("/listings/mine", { signal }, { rejectUnauthorized: signal !== undefined });
  },

  async reopenPost(kind: PostKind, id: string) {
    const reply = kind === "item"
      ? await req<{ item: OwnerPostSummary }>(`/items/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ status: "active" }) }, { rejectUnauthorized: true })
      : await req<{ item: OwnerPostSummary }>(`/listings/${kind}/${encodeURIComponent(id)}/reopen`, { method: "POST" }, { rejectUnauthorized: true });
    if (!reply) throw new Error("Could not reopen this post. Try again.");
    return reply;
  },

  async closePost(kind: Exclude<PostKind, "item">, id: string) {
    return req<{ item: OwnerPostSummary }>(`/listings/${kind}/${encodeURIComponent(id)}/close`, { method: "POST" }, { rejectUnauthorized: true });
  },

  // Close retains existing dealings; Reopen clears only eligible owner closure.
  // kind ∈ job | service | project | rfq | company | resume
  async archiveListing(kind: string, id: string) {
    return req<{ item: any; archived: boolean }>(`/listings/${kind}/${encodeURIComponent(id)}/archive`, { method: "POST" });
  },

  async closeService(id: string) {
    return req<{ item: { id: string; status: "open" | "closed" | "archived"; hidden: boolean }; archived: boolean }>(`/listings/service/${encodeURIComponent(id)}/close`, { method: "POST" });
  },

  async restoreListing(kind: string, id: string) {
    return req<{ item: any; archived: boolean }>(`/listings/${kind}/${encodeURIComponent(id)}/restore`, { method: "POST" });
  },

  async deleteListing(kind: string, id: string) {
    return req<{ ok: boolean; deleted: string; user?: AuthUser }>(`/listings/${kind}/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  // Restore an archived item (seller marketplace) — sets status back to active.
  async restoreItem(id: string) {
    return req<{ item: any }>(`/items/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ status: "active" }) });
  },

  async getListing(kind: "people" | "services" | "companies" | "jobs" | "projects" | "rfqs", id: string) {
    const r = await req<{ item: any }>(`/marketplace/${kind}/${encodeURIComponent(id)}`);
    return r?.item ?? null;
  },

  // v3.9.35 — PSGC geography (cascading location picker, CEO #5)
  async geoRegions() {
    const r = await req<{ items?: { code: string; name: string }[] }>("/geo/regions");
    return { items: Array.isArray(r?.items) ? r.items : [] };
  },

  async geoProvinces(region: string) {
    const r = await req<{ items?: { code: string; name: string }[] }>(`/geo/provinces?region=${encodeURIComponent(region)}`);
    return { items: Array.isArray(r?.items) ? r.items : [] };
  },

  async geoCities(province: string) {
    const r = await req<{ items?: { code: string; name: string }[] }>(`/geo/cities?province=${encodeURIComponent(province)}`);
    return { items: Array.isArray(r?.items) ? r.items : [] };
  },

  async geoBarangays(city: string) {
    const r = await req<{ items?: { code: string; name: string }[] }>(`/geo/barangays?city=${encodeURIComponent(city)}`);
    return { items: Array.isArray(r?.items) ? r.items : [] };
  },

  async savedKeys() {
    const r = await req<{ keys: string[] }>("/saved/keys");
    return r?.keys ?? [];
  },

  async savedList(signal?: AbortSignal) {
    const r = await req<{ saved: any[] }>("/saved", { signal }, { rejectUnauthorized: signal !== undefined });
    if (signal && !Array.isArray(r?.saved)) throw new Error("Missing saved rows.");
    return r?.saved ?? [];
  },

  async saveListing(kind: string, refId: string) {
    return req<{ ok: boolean; saved: boolean }>("/saved", { method: "POST", body: JSON.stringify({ kind, refId }) });
  },

  async unsaveListing(kind: string, refId: string) {
    return req<{ ok: boolean; saved: boolean }>(`/saved/${encodeURIComponent(kind)}/${encodeURIComponent(refId)}`, { method: "DELETE" });
  },

  // Increment 19: unified search
  async search(q: string, type?: string, signal?: AbortSignal) {
    const qs = new URLSearchParams({ q });
    if (type) qs.set("type", type);
    return req<{ query: string; results: any[]; counts: Record<string, number>; total: number }>(`/search?${qs.toString()}`, { signal }, { rejectUnauthorized: signal !== undefined });
  },

  async geoCityOptions() {
    const r = await req<{ items?: { code: string; name: string }[] }>("/geo/city-options");
    return { items: Array.isArray(r?.items) ? r.items : [] };
  }
};
